/**
 * Ni² Intent Router
 *
 * Ni² freeform input goes through this router BEFORE the generic
 * canned-response pipeline. If a structured intent matches, the
 * caller renders the corresponding structured card (M&M, log new
 * case, pre-case review, …) and skips the canned answer.
 *
 * Design goals:
 *  • additive & reusable — no phrase matching lives inside
 *    Ni2Workspace itself;
 *  • extensible — new intents (LOG_NEW_CASE, UPDATE_EXISTING_CASE,
 *    PRE_CASE_REVIEW) plug in via one new entry;
 *  • provenance-shaped — every match resolves a workbook Case ID
 *    when the intent implies one so downstream cards can render
 *    real metadata immediately.
 */
import { getCases } from "@/data/repository";
import { MM_BUILDER_ENABLED } from "@/constants/featureFlags";

const RULES = [
  {
    intent: "ADD_TO_MM",
    enabled: () => MM_BUILDER_ENABLED,
    patterns: [
      /add\s+(?:today'?s\s+)?(?:the\s+)?(.*?)\s+(?:case\s+)?to\s+(?:the\s+)?m\s?&?\s?m/i,
      /put\s+(?:today'?s\s+)?(?:the\s+)?(.*?)\s+(?:case\s+)?(?:on|in)\s+m\s?&?\s?m/i,
      /queue\s+(.*?)\s+for\s+m\s?&?\s?m/i,
    ],
    resolve: (match, text) => {
      const hint = String(match?.[1] || "").toLowerCase();
      const cases = getCases();
      const scored = cases
        .map((c) => {
          const hay = [c.diagnosis, c.pathologySubtype, c.laterality, c.caseId]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          const score = hint
            .split(/\s+/)
            .filter((w) => w.length > 2)
            .reduce((s, w) => (hay.includes(w) ? s + 1 : s), 0);
          return { c, score };
        })
        .sort((a, b) => b.score - a.score);
      const best = scored[0]?.score > 0 ? scored[0].c : cases.find((c) => c.caseId === "VS-RAO-001") || cases[0];
      const tagSenior = /senior\s+resident|tag\s+.*resident|@.*resident/i.test(text);
      return { caseId: best?.caseId, tagSenior, rawText: text };
    },
  },
  {
    intent: "RECALL_DEPT_MEMORY",
    enabled: () => MM_BUILDER_ENABLED,
    patterns: [
      /(?:how\s+do\s+we|what\s+does\s+the\s+department|departmental\s+practice).*(?:facial\s+nerve\s+monitoring|monitoring\s+decline|facial\s+emg)/i,
      /(?:manage|handle|approach).*facial\s+nerve\s+monitoring.*(?:decline|drop|decrement)/i,
      /facial\s+nerve\s+monitoring\s+(?:decline|drop|decrement)/i,
    ],
    resolve: (match, text) => ({ query: text }),
  },
];

export function matchNi2Intent(text) {
  const trimmed = String(text || "").trim();
  if (!trimmed) return null;
  for (const rule of RULES) {
    if (!rule.enabled()) continue;
    for (const pattern of rule.patterns) {
      const m = pattern.exec(trimmed);
      if (m) {
        return { intent: rule.intent, payload: rule.resolve(m, trimmed) };
      }
    }
  }
  return null;
}
